import Model from "../../../../model/model"
import {
  FleetingIdea,
  Idea,
  ID,
  Project,
  WebSource,
  ideaIsEmpty,
} from "../../../../model/types"
import { canUseReadingPane } from "../sources_page/source_content"
import { previewFromHtml } from "./move_card"
import { MoveKey } from "./types"

/** True when editor HTML has no visible text. */
export const blankContent = (
  content: string | null | undefined,
): boolean => {
  if (content == null || content === undefined || content === "") return true
  if (
    content === "<p></p>" ||
    content === "<p><br></p>" ||
    content === "<p><br/></p>"
  )
    return true
  return previewFromHtml(content, 40) === ""
}

export const blankTitle = (title: string | null | undefined): boolean =>
  title == null || title === undefined || title.trim() === ""

/** No source set — `self` (“myself”) and `web` both count as having a source. */
export const missingSource = (idea: Idea): boolean =>
  idea.sourceType == null || idea.sourceType === undefined

export type PolishGap = "title" | "content" | "source"

/** Gaps that make a permanent idea eligible for Polish. */
export const polishGaps = (idea: Idea): PolishGap[] => {
  const gaps: PolishGap[] = []
  if (blankTitle(idea.title)) gaps.push("title")
  if (blankContent(idea.content)) gaps.push("content")
  // Goals rarely have sources; don't treat that as something to polish
  if (idea.type !== "goal" && missingSource(idea)) gaps.push("source")
  return gaps
}

export const eligibleReadSources = (model: Model): WebSource[] =>
  model
    .getSources()
    .filter(
      (s) =>
        (s.status === "new" || s.status === "queued") && canUseReadingPane(s),
    )

export const eligibleExtractSources = (model: Model): WebSource[] =>
  model
    .getSources()
    .filter(
      (s) =>
        (s.status === "new" || s.status === "queued") &&
        !blankContent(s.literatureNotes),
    )

export const eligibleFleetingIdeas = (model: Model): FleetingIdea[] =>
  model.getFleetingIdeas()

/** Permanent ideas missing title, content, and/or source. */
export const eligiblePolishIdeas = (model: Model): Idea[] =>
  Array.from(model.store.ideas.values()).filter(
    (idea) =>
      (idea.type === "idea" || idea.type === "goal") &&
      polishGaps(idea).length > 0,
  )

export const eligibleWanderIdeas = (model: Model): Idea[] =>
  Array.from(model.store.ideas.values()).filter(
    (idea) =>
      (idea.type === "idea" || idea.type === "goal") && !ideaIsEmpty(idea),
  )

export const eligibleProjects = (model: Model): Project[] =>
  model.getProjects().filter((p) => p.status === "in_progress")

export const eligibleIdsFor = (move: MoveKey, model: Model): ID[] => {
  switch (move) {
    case "read":
      return eligibleReadSources(model).map((s) => s.id)
    case "extract":
      return eligibleExtractSources(model).map((s) => s.id)
    case "place":
      return eligibleFleetingIdeas(model).map((f) => f.id)
    case "polish":
      return eligiblePolishIdeas(model).map((i) => i.id)
    case "wander":
      return eligibleWanderIdeas(model).map((i) => i.id)
    case "project":
      return eligibleProjects(model).map((p) => p.id)
  }
}
